import {
  Button,
  Chip,
  Collapse,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  InputLabel,
  makeStyles,
  MenuItem,
  MenuProps,
  Select,
  TextField,
  Theme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Alert from "@material-ui/lab/Alert";
import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { City } from "../../interfaces";
import {
  beachActions,
  beachSelectors,
  cityActions,
  citySelectors,
} from "../../redux";
import useCityForm, { CityFormType } from "./useCityForm";

interface FormDialogCityProps {
  cityToUpdate?: City;
  onClose: () => any;
  isOpen: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chips: {
      display: "flex",
      flexWrap: "wrap",
    },
    chip: {
      margin: 2,
    },
    label: {
      marginTop: theme.spacing(2),
    },
  })
);

const FormDialogCity: React.FC<FormDialogCityProps> = ({
  cityToUpdate,
  onClose,
  isOpen,
}) => {
  const [alertOpen, setOpen] = React.useState(true);
  const classes = useStyles();

  const {
    errors,
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
  } = useCityForm();
  const [hasBeenRequested, setHasBeenRequested] = useState(false);
  const dispatch = useDispatch();
  const createdCity = useSelector(citySelectors.getCreatedCity);
  const updatedCity = useSelector(citySelectors.getUpdatedCity);
  const beaches = useSelector(beachSelectors.getAllBeaches);
  const menuProps: Partial<MenuProps> | undefined = {
    PaperProps: {
      variant: "outlined",
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  useEffect(() => {
    if (cityToUpdate) {
      reset({
        istat: cityToUpdate.istat,
        id: cityToUpdate.id,
        name: cityToUpdate.name,
        province: cityToUpdate.province,
        region: cityToUpdate.region,
        beaches: cityToUpdate.beaches,
      });
    }
  }, [cityToUpdate, reset]);

  useEffect(() => {
    if (!isOpen) {
      reset({});
      dispatch(cityActions.clear("updatedCity"));
      setHasBeenRequested(false);
    }
  }, [dispatch, isOpen, reset]);

  useEffect(() => {
    dispatch(beachActions.loadAllBeach({}));
  }, [dispatch]);

  useEffect(() => {
    if (createdCity.data || updatedCity.data) {
      reset({});
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  }, [createdCity, updatedCity, reset, onClose]);

  const onSubmit = (data: CityFormType) => {
    if (cityToUpdate) {
      dispatch(
        cityActions.updateCity({
          id: data.id,
          istat: data.istat,
          name: data.name,
          province: data.province,
          region: data.region,
          beaches: data.beaches,
        })
      );
    } else
      dispatch(
        cityActions.createCity({
          id: "",
          istat: data.istat,
          name: data.name,
          province: data.province,
          region: data.region,
          beaches: data.beaches,
        })
      );
    setHasBeenRequested(true);
    dispatch(cityActions.loadAllCity({}));
  };

  const beachesOptions = beaches.data?.results.map((beach) => ({
    value: beach.id,
    label: beach.name,
  }));

  return (
    <Dialog open={isOpen} onClose={onClose} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">Aggiungi nuova città</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <input name="id" ref={register} type="hidden"></input>
        <DialogContent>
          <DialogContentText>
            <Collapse
              in={
                !createdCity.isLoading &&
                !updatedCity.isLoading &&
                hasBeenRequested &&
                alertOpen
              }
            >
              <Alert
                color={
                  createdCity.error || updatedCity.error ? "error" : "success"
                }
                severity={createdCity.error ? "error" : "success"}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setOpen(false);
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {createdCity.error
                  ? "Si è verificato un errore"
                  : "Dato aggiunto correttamente"}
              </Alert>
            </Collapse>
          </DialogContentText>
          <TextField
            label="Codice Istat"
            helperText={errors.istat?.message}
            fullWidth
            error={!!errors.istat}
            name="istat"
            inputRef={register}
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
          <TextField
            label="Nome completo"
            helperText={errors.name?.message}
            error={!!errors.name}
            name="name"
            inputRef={register}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />

          <TextField
            label="Provincia"
            helperText={errors.province?.message}
            fullWidth
            error={!!errors.province}
            name="province"
            inputRef={register}
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
          <TextField
            label="Regione"
            helperText={errors.region?.message}
            fullWidth
            error={!!errors.region}
            name="region"
            inputRef={register}
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
          {beachesOptions && (
            <Controller
              as={
                <FormControl fullWidth className={classes.label}>
                  <InputLabel id="multiple-beaches-label">Spiagge</InputLabel>
                  <Select
                    onChange={(evt) => {
                      setValue("beaches", evt.target.value as string[]);
                    }}
                    fullWidth
                    labelId="multiple-beaches-label"
                    id="multiple-beaches-chip"
                    multiple
                    defaultValue={getValues("beaches") || []}
                    input={<Input id="select-multiple-beaches" />}
                    renderValue={(selected) => (
                      <div className={classes.chips}>
                        {(selected as string[]).map((value) => (
                          <Chip
                            key={value}
                            label={
                              beachesOptions.find((f) => f.value === value)
                                ?.label
                            }
                            className={classes.chip}
                          />
                        ))}
                      </div>
                    )}
                    MenuProps={menuProps}
                  >
                    {beachesOptions
                      ? beachesOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      : []}
                  </Select>
                  <FormHelperText error={!!errors.beaches}>
                    {errors.beaches ? errors.beaches.message : ""}
                  </FormHelperText>
                </FormControl>
              }
              name="beaches"
              control={control}
              defaultValue={[]}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Chiudi
          </Button>
          <Button type="submit" color="primary">
            Aggiungi
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FormDialogCity;
